import React, { useEffect, forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import { classNames, useGlobalId, useUpdateRef, useSetState } from '@zambezi/sdk/common'
import { DatePicker, allowedFormats } from '@zambezi/sdk/date-picker'
import { getError, errorOptionsType, hintOptionsType } from './zambezi-helpers'
import Label from './Label'
const noop = () => {}

/**
 * @type {React.ForwardRefRenderFunction<React.Ref<HTMLInputElement>, import('./interfaces').DateFieldProps>}
 * @param {import('./interfaces').DateFieldProps} props
 * @param {React.Ref<HTMLInputElement>} innerRef
 */
function dateField (props, innerRef) {
  const {
    value,
    defaultValue,
    name,
    id: propsId,
    label,
    rules,
    className,
    autoFocus,
    error: propError,
    errorOptions,
    errorMessageShown,
    disabled,
    required,
    hint,
    hintOptions,
    readOnly,
    inputProps = {},
    ignoreErrors,
    onChange,
    onError,
    onBlur,
    onFocus,
    onInputChange,
    errorInline,
    withAsterisk
  } = props

  const errorId = useGlobalId('zb-formidable-error-')
  const globalId = useGlobalId('zb-control-id-')
  const id = propsId || globalId
  const elementRef = useRef()
  const [state, setState] = useSetState({
    value: 'value' in props ? value : defaultValue,
    error: propError
  })
  const { value: stateValue, error: stateError } = state

  const inputOptions = Object.assign({}, inputProps, {
    className: classNames(
      {
        'zb-date-picker-is-error': errorMessageShown(stateError)
      },
      className
    ),
    name,
    id,
    date: stateValue,
    error: !!stateError,
    onChange: handleChange,
    onError: handleError,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onInputChange: handleInputChange
  })

  const labelOptions = {
    inputOptions,
    hint,
    hintOptions,
    label,
    error: stateError,
    errorOptions
  }

  const errorMessage = errorMessageShown(stateError)
    ? getError(stateError, errorOptions, errorId, errorInline)
    : null

  function handleChange (value) {
    setState({
      error: null,
      value
    })
    onChange(name, value)
  }

  function handleInputChange (value) {
    setState({
      error: propError || null
    })
    onInputChange(name, value)
  }

  function handleError ({ message }, value) {
    if (!ignoreErrors) {
      setState({
        error: propError || message
      })
    }

    onError(name, value, stateError)
  }

  function handleBlur () {
    onBlur(name, stateValue)
  }

  function handleFocus () {
    onFocus(name, stateValue)
  }

  useEffect(() => setState({ value: value || undefined }), [value])
  useEffect(() => setState({ error: propError }), [propError])
  useUpdateRef(innerRef, elementRef, { ...props })

  if (autoFocus) inputOptions.autoFocus = autoFocus
  if (readOnly) inputOptions.readOnly = readOnly
  if (disabled) inputOptions.disabled = disabled
  if (required) inputOptions.required = required
  if (id) inputOptions.id = id
  if (stateError) inputOptions['aria-invalid'] = true
  if (errorMessage) inputOptions['aria-describedby'] = errorMessage.props.id

  return (
    <div className='zb-control-wrap'>
      {label ? <Label {...labelOptions}>{label} {required && withAsterisk && <sup className='zb-asterisk'>*</sup>}</Label> : null}
      <div className={classNames('zb-control', { 'is-error': stateError })}>
        <input
          type='hidden'
          ref={elementRef}
          rules={rules}
          value={stateValue || ''}
        />
        <DatePicker {...inputOptions} />
      </div>
      {errorMessage}
    </div>
  )
}

const DateField = forwardRef(dateField)

DateField.displayName = 'DateField'

DateField.allowedFormats = allowedFormats

DateField.propTypes = {
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onError: PropTypes.func,
  onInputChange: PropTypes.func,
  error: PropTypes.any,
  errorOptions: errorOptionsType,
  errorMessageShown: PropTypes.func,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.instanceOf(Date),
  defaultValue: PropTypes.instanceOf(Date),
  className: PropTypes.string,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  autoFocus: PropTypes.bool,
  readOnly: PropTypes.bool,
  hint: PropTypes.string,
  hintOptions: hintOptionsType,
  inputProps: PropTypes.object,
  ignoreErrors: PropTypes.bool,
  errorInline: PropTypes.bool,
  withAsterisk: PropTypes.bool
}

DateField.defaultProps = {
  value: undefined,
  defaultValue: undefined,
  required: false,
  errorOptions: { arrowDirection: 'top' },
  ignoreErrors: false,
  withAsterisk: false,
  errorMessageShown: noop,
  onFocus: noop,
  onChange: noop,
  onInputChange: noop,
  onBlur: noop,
  onError: noop
}

export default DateField
export { DateField, allowedFormats }
